// Supervisor Login Page — Phone number only

import React, { useState } from 'react';
import { FiPhone, FiLogIn } from 'react-icons/fi';
import { capcrmlogo } from '../assets';
import axiosInstance from '../api/axiosInstance';
import { API_ENDPOINTS } from '../config/api.config';

const SupervisorLoginPage = () => {
  const [phone,    setPhone]    = useState('');
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const [phoneErr, setPhoneErr] = useState('');

  const handlePhone = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
    setPhone(digits);
    if (phoneErr) setPhoneErr('');
    if (error)    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!phone.trim()) { setPhoneErr('Phone number is required'); return; }
    if (phone.length !== 10) { setPhoneErr('Enter exactly 10 digits'); return; }

    setLoading(true);
    setError('');
    try {
      const res = await axiosInstance.post(API_ENDPOINTS.SUPERVISOR_AUTH.LOGIN, {
        phone_number: `+91 ${phone}`,
      });
      // Store supervisor token and info for use by the supervisor app
      const { token, supervisor } = res.data;
      localStorage.setItem('supervisor_token', token);
      localStorage.setItem('supervisor_info', JSON.stringify(supervisor));
      // Redirect to supervisor app home (adjust as needed)
      window.location.href = '/';
    } catch (err) {
      const data = err.response?.data;
      setError(
        typeof data === 'string'
          ? data
          : data?.detail || data?.phone_number?.[0] || 'Login failed. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex">
      {/* Left branding panel */}
      <div className="hidden lg:flex lg:w-5/12 xl:w-1/2 flex-col justify-between p-12 bg-gradient-to-br from-sky-700 via-cyan-800 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/5" />
          <div className="absolute bottom-0 -left-20 w-80 h-80 rounded-full bg-white/5" />
        </div>

        {/* Logo */}
        <div className="relative flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center">
            <img src={capcrmlogo} alt="CAP" className="w-full h-full object-contain" />
          </div>
          <div>
            <p className="text-white font-bold text-base leading-tight">CAP Supervisor</p>
            <p className="text-sky-300 text-xs">Coimbatore Auto Parts</p>
          </div>
        </div>

        {/* Middle content */}
        <div className="relative space-y-6">
          <div className="w-12 h-1 bg-gradient-to-r from-sky-400 to-transparent rounded-full" />
          <h2 className="text-4xl xl:text-5xl font-black text-white leading-tight">
            Supervisor<br />
            <span className="text-sky-300">portal</span><br />
            access.
          </h2>
          <p className="text-slate-300 text-base max-w-sm">
            Sign in with your registered phone number to manage your assigned inquiries and quotes.
          </p>
        </div>

        <p className="text-slate-400 text-xs relative">
          © {new Date().getFullYear()} Coimbatore Auto Parts. All rights reserved.
        </p>
      </div>

      {/* Right login panel */}
      <div className="flex-1 flex items-center justify-center p-6 bg-white">
        <div className="w-full max-w-sm animate-fade-in-up">
          {/* Mobile logo */}
          <div className="flex items-center gap-3 mb-10 lg:hidden">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center">
              <img src={capcrmlogo} alt="CAP" className="w-full h-full object-contain" />
            </div>
            <div>
              <p className="font-bold text-slate-800">CAP Supervisor</p>
              <p className="text-slate-400 text-xs">Coimbatore Auto Parts</p>
            </div>
          </div>

          <h1 className="text-2xl font-bold text-slate-900 mb-1">Welcome back</h1>
          <p className="text-slate-500 text-sm mb-8">Enter your phone number to sign in.</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 text-rose-600 text-sm font-medium">
                {error}
              </div>
            )}

            {/* Phone field */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                Phone Number
              </label>
              <div className={`flex rounded-xl overflow-hidden border transition-all focus-within:ring-2 ${
                phoneErr
                  ? 'border-rose-400 focus-within:ring-rose-300'
                  : 'border-slate-200 focus-within:ring-sky-300 focus-within:border-sky-400'
              }`}>
                <span className="flex items-center gap-1.5 px-3 bg-slate-100 text-slate-500 text-sm font-semibold border-r border-slate-200 shrink-0 select-none">
                  <FiPhone className="w-3.5 h-3.5" />
                  +91
                </span>
                <input
                  type="tel"
                  inputMode="numeric"
                  placeholder="98765 43210"
                  value={phone}
                  onChange={handlePhone}
                  disabled={loading}
                  maxLength={10}
                  autoFocus
                  className="flex-1 px-3 py-3 text-sm text-slate-800 placeholder-slate-400 bg-white focus:outline-none"
                />
                <span className={`flex items-center px-3 text-xs font-medium ${
                  phone.length === 10 ? 'text-emerald-600' : 'text-slate-400'
                }`}>
                  {phone.length}/10
                </span>
              </div>
              {phoneErr && <p className="text-rose-500 text-xs mt-1 font-medium">{phoneErr}</p>}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-gradient-to-r from-sky-600 to-cyan-600 hover:from-sky-700 hover:to-cyan-700 text-white font-semibold rounded-xl shadow-lg shadow-sky-500/20 transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <svg className="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : (
                <FiLogIn className="w-4 h-4" />
              )}
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
          </form>

          <p className="text-slate-400 text-xs text-center mt-8">
            © {new Date().getFullYear()} Coimbatore Auto Parts · All rights reserved
          </p>
        </div>
      </div>
    </div>
  );
};

export default SupervisorLoginPage;
